import { useTranslation } from 'react-i18next';

import { contentSelectors } from '../../../models/content';
import { usePatronSelector } from '../../../common/use-patron-selector';
import { useContentLikes } from '../../../common/use-content-likes';

import { uiOperations } from '../../../models/ui';
import { ID as ViewLikesModalID } from './view-likes-modal/old/view-likes-modal';
import useFeatureFlag from '../../../common/use-feature-flag';
import useScreenSize from '../../../common/use-screen-size';
import { Feature } from '../../../models/features/features';
import {
  Button,
  ModalRoot,
  ModalTriggerButton,
} from '@socialchorus/shared-ui-components';
import { programSelectors } from '../../../models/program';
import { useDispatch } from 'react-redux';
import { ViewLikesModal } from './view-likes-modal/view-likes-modal';

//this cannot be generalized, it is tuned for this specific component and the overall text inside it, and it also assumed only a single name.
const MOBILE_SHORT_TRUNCATED_NAME_LENGTH = 10;
const MOBILE_MEDIUM_TRUNCATED_NAME_LENGTH = 20;
const MOBILE_LONG_TRUNCATED_NAME_LENGTH = 30;
const MOBILE_SHORT_NAME_MAX_WIDTH = 400;
const MOBILE_MEDIUM_NAME_MAX_WIDTH = 600;

const truncateByScreenSize = (name: string, width: number) => {
  if (width < MOBILE_SHORT_NAME_MAX_WIDTH) {
    return name.length > MOBILE_SHORT_TRUNCATED_NAME_LENGTH
      ? `${name.substring(0, MOBILE_SHORT_TRUNCATED_NAME_LENGTH)}...`
      : name;
  } else if (width < MOBILE_MEDIUM_NAME_MAX_WIDTH) {
    return name.length > MOBILE_MEDIUM_TRUNCATED_NAME_LENGTH
      ? `${name.substring(0, MOBILE_MEDIUM_TRUNCATED_NAME_LENGTH)}...`
      : name;
  } else {
    return name.length > MOBILE_LONG_TRUNCATED_NAME_LENGTH
      ? `${name.substring(0, MOBILE_LONG_TRUNCATED_NAME_LENGTH)}...`
      : name;
  }
};

type ContentActionViewLikeProps = {
  contentId: number;
};

const ContentActionViewLike = ({ contentId }: ContentActionViewLikeProps) => {
  const dispatch = useDispatch();
  //controls minor layout changes associated with the reaction types deployment.
  const expandedReactionTypesDeployed = useFeatureFlag(
    Feature.EXPANDED_REACTION_TYPES_DEPLOY
  );
  //controls the reaction types feature
  const expandedReactionTypesEnabled = usePatronSelector((state) =>
    programSelectors.getExpandedReactionTypesEnabled(state)
  );

  const likeCount = usePatronSelector((s) =>
    contentSelectors.getContentLikeCount(s, { contentId })
  );
  const reactions = usePatronSelector((state) =>
    contentSelectors.getContentReactions(state, { contentId })
  );
  const isLiked = usePatronSelector((state) =>
    contentSelectors.getContentLiked(state, { contentId })
  );
  const reactionCount = reactions?.reduce(
    (acc, reaction) => acc + reaction.count,
    0
  );
  const top3ReactionTypes = reactions
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);

  // isLiked is optimistically updated on every action — use it as the source of truth
  const userReacted = isLiked;

  const tescoQ3 = useFeatureFlag(Feature.TESCO_Q3);
  const { isMobile, width } = useScreenSize();

  const { likes, isLoading } = useContentLikes({ contentId }); //includes reactions

  const { t } = useTranslation();

  const openOldViewLikesModal = () =>
    dispatch(uiOperations.displayOverlay(ViewLikesModalID, { contentId }));

  if (
    isLoading ||
    ((likeCount === 0 || likes.length === 0) && !reactionCount)
  ) {
    return null;
  }

  if ((expandedReactionTypesEnabled && reactionCount < 1) || likeCount < 1) {
    return null;
  }

  let textLabel: string;

  if (tescoQ3 && isMobile && !expandedReactionTypesEnabled) {
    textLabel = t('content.liked_compact', {
      count: likeCount,
    });
  } else {
    if (expandedReactionTypesEnabled) {
      if (userReacted && reactionCount === 1) {
        textLabel = t('content.you_reacted');
      } else if (userReacted && reactionCount === 2) {
        textLabel = t('content.you_reacted_and_one', {
          remainder: reactionCount - 1,
        });
      } else if (userReacted) {
        textLabel = t('content.you_reacted_and_other', {
          remainder: reactionCount - 1,
        });
      } else if (reactionCount === 1) {
        const name = likes[0]?.name;
        const truncatedName = isMobile
          ? truncateByScreenSize(name, width)
          : name;

        textLabel = t('content.others_reacted_by_one', {
          name1: truncatedName,
        });
      } else if (reactionCount > 1 && isMobile) {
        const name = likes[0]?.name;
        const truncatedName = isMobile
          ? truncateByScreenSize(name, width)
          : name;

        textLabel = t('content.others_reacted_by_many_short', {
          name1: truncatedName,
          remainder: reactionCount - 1,
        });
      } else if (reactionCount === 2) {
        textLabel = t('content.others_reacted_by_two', {
          name1: likes[0]?.name,
          name2: likes[1]?.name,
        });
      } else {
        textLabel = t('content.others_reacted_by_many', {
          name1: likes[0]?.name,
          name2: likes[1]?.name,
          remainder: reactionCount - 2,
        });
      }
    } else {
      if (likeCount === 1) {
        textLabel = t('content.liked_by_one', {
          name1: likes[0]?.name,
          interpolation: { escapeValue: false },
        });
      } else if (likeCount === 2) {
        textLabel = t('content.liked_by_two', {
          name1: likes[0]?.name,
          name2: likes[1]?.name,
          interpolation: { escapeValue: false },
        });
      } else {
        textLabel = t('content.liked_by_many', {
          name1: likes[0]?.name,
          name2: likes[1]?.name,
          remainder: likeCount - 2,
          interpolation: { escapeValue: false },
        });
      }
    }
  }

  if (!expandedReactionTypesDeployed) {
    return (
      <Button
        variant="text"
        aria-label={t('content.view_likes_action')}
        onClick={openOldViewLikesModal}
        asChild
      >
        <span
          dangerouslySetInnerHTML={{
            __html: textLabel,
          }}
        />
      </Button>
    );
  }

  return (
    <ModalRoot>
      <ModalTriggerButton
        variant="text"
        aria-label={
          expandedReactionTypesEnabled
            ? t('content.view_reactions_action')
            : t('content.view_likes_action')
        }
        size={isMobile ? 'compact' : 'default'}
        buttonLabelAsChild
      >
        <div className="content-action__view-likes-label-container">
          {expandedReactionTypesEnabled
            ? top3ReactionTypes.map((r) => <span key={r.id}>{r.emoji}</span>)
            : null}
          <span
            style={{ fontWeight: 500 }} //hacky way to ignore any bolding. doing it dirty while pending a design review. If we go with this, should refactor to not use dangerouslySetInnerHTML.
            dangerouslySetInnerHTML={{
              __html: textLabel,
            }}
          />
        </div>
      </ModalTriggerButton>
      <ViewLikesModal contentId={contentId} />
    </ModalRoot>
  );
};

export default ContentActionViewLike;
